import { useEffect, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { Forklift } from "./primitives/Forklift";
import { WarehouseModel } from "./WarehouseModel";
import { useTelemetryStore, getDisplayStatus, gatewayOnlineForAsset, assetShownOnSite } from "../stores/telemetryStore";
import { useSiteStore } from "../stores/siteStore";
import { withParkedPoses } from "./parking";
import { useUIStore } from "../stores/uiStore";
import { useHistoryStore } from "../stores/historyStore";
import { EditableWarehouseGroup } from "./EditableWarehouseGroup";
import { ZonesLayer } from "./ZonesLayer";
import { useSiteId } from "../hooks/useSiteId";
import { useWarehouseId } from "../hooks/useWarehouseId";
import { claimLiveHeatSeed, getLiveHeatField, recordLivePoses, resetLiveHeat, SINK_HZ } from "./heatField";
import type { FloorFrame } from "../hooks/useFloorFrame";
import { DEMO_FRAME } from "../hooks/useFloorFrame";
import { fetchHeatmap } from "../api/rest";
import { loadFloorFrame } from "../hooks/useFloorFrame";
// Demo floor dims only (heat-field extent + fallback model size). Zone SHAPES
// no longer come from this file — Scene.tsx mounts ZonesLayer on the zones
// API instead (ADR 143).
import zonesJson from "../../../infra/seed/zones.json";

const WIDTH_M  = zonesJson.widthM  ?? 60;
const HEIGHT_M = zonesJson.heightM ?? 40;

// Samples all live forklift positions into the history accumulator every
// SAMPLE_EVERY frames (~1 s at 60 fps). Runs inside the R3F loop so it
// requires no extra timer and naturally stops when the Canvas is unmounted.
const SAMPLE_EVERY = 60;
function LiveAccumulator({ floor }: { floor: FloorFrame }) {
  const frame = useRef(0);
  const accumulateLive = useHistoryStore((s) => s.accumulateLive);
  useFrame(() => {
    if (++frame.current % SAMPLE_EVERY !== 0) return;
    // Snapshot positions inside the frame (cheap), then push the Zustand
    // update off-frame so the resulting React re-render doesn't extend rAF.
    const forklifts = Object.values(useTelemetryStore.getState().forklifts);
    setTimeout(() => accumulateLive(forklifts), 0);
  });

  // Heat field feeds at full delta rate (~11 Hz per asset) — dwell weighting
  // lives in recordLivePoses, so extra store updates (selection etc.) are
  // deduped by lastSeenMs. Replay scrubbing pushes synthetic deltas through
  // the store; the mode guard keeps those out of the live field.
  useEffect(() => {
    return useTelemetryStore.subscribe((s) => {
      if (useHistoryStore.getState().mode !== "live") return;
      recordLivePoses(getLiveHeatField(floor.widthM, floor.heightM, floor.originX, floor.originY),
                      Object.values(s.forklifts), Date.now());
    });
  }, [floor]);
  return null;
}

export function Warehouse() {
  const SITE_ID = useSiteId();
  const WAREHOUSE_ID = useWarehouseId();
  // Dwell from another site/warehouse must not bleed into this one's heatmap.
  useEffect(() => { resetLiveHeat(); }, [SITE_ID, WAREHOUSE_ID]);

  // Poses are only splatted once this resolves; until then the fallback frame
  // is the demo floor, which is exactly the grid that used to swallow them.
  const [frame, setFrame] = useState<FloorFrame>(DEMO_FRAME);
  useEffect(() => {
    if (!SITE_ID || !WAREHOUSE_ID) return;
    let cancelled = false;
    loadFloorFrame(SITE_ID, WAREHOUSE_ID).then((f) => { if (!cancelled) setFrame(f); });
    return () => { cancelled = true; };
  }, [SITE_ID, WAREHOUSE_ID]);

  // Backfill the live heat field with the last 30 min from ClickHouse so a
  // fresh tab doesn't open blank; live splats take over from `to` onwards.
  useEffect(() => {
    if (!SITE_ID || !WAREHOUSE_ID) return;
    const field = getLiveHeatField(frame.widthM, frame.heightM, frame.originX, frame.originY);
    if (!claimLiveHeatSeed()) return;
    const to = Date.now();
    fetchHeatmap(SITE_ID, to - 30 * 60_000, to, 0.5, WAREHOUSE_ID)
      .then((cells) => { for (const c of cells) field.splat(c.x, c.z, c.count / SINK_HZ, to); })
      .catch(() => { /* no history service in offline dev — live-only field */ });
  }, [SITE_ID, WAREHOUSE_ID, frame]);
  const forklifts   = useTelemetryStore((s) => s.forklifts);
  const gateways    = useTelemetryStore((s) => s.gateways);
  const layers      = useUIStore((s) => s.layers);
  const hiddenTypes = useUIStore((s) => s.hiddenAssetTypes);
  const hasRtls     = useSiteStore((s) => s.hasCapability("rtls"));

  return (
    <EditableWarehouseGroup>
      <LiveAccumulator floor={frame} />
      {/* ADR 210 lighting: a single fixed key light gives real form/depth, a
          lifted hemisphere + low ambient keep the shadow side READABLE (never
          black), and NO light casts a shadow. "Can't see the back when I orbit"
          is solved by the camera headlight in Scene.tsx (world space) — the face
          toward you is always lit — instead of flattening the scene (ADR 209,
          reverted for looking unreal and too bright). */}
      <ambientLight intensity={0.28} />
      <hemisphereLight args={["#9db4d8", "#3f4e6c", 0.5]} />
      <directionalLight position={[50, 80, 30]} intensity={0.9} />

      {/* No ground plane/grid: the uploaded warehouse GLB provides the floor; a coplanar
          plane at y=0 z-fights the model floor (flicker). Without a model there is no
          floor at all — the /live EmptyState steers the user to upload one (ADR 184). */}

      {/* GLB warehouse model — per-warehouse GLB fetched from MinIO; renders nothing until one is uploaded. */}
      <WarehouseModel widthM={WIDTH_M} heightM={HEIGHT_M} siteId={SITE_ID} warehouseId={WAREHOUSE_ID} />

      {/* DB zone floor markings (ADR 143). Inside EditableWarehouseGroup on
          purpose — zones share the warehouse frame with poses and the GLB, so
          they must ride the same layout transform (the old static ZoneFloor
          did). key: fresh instance per site/warehouse switch, same convention
          as OccupancyLayerContainer. */}
      {layers.zones && (
        <ZonesLayer
          key={`${SITE_ID}:${WAREHOUSE_ID}`}
          siteId={SITE_ID}
          warehouseId={WAREHOUSE_ID ?? ""}
        />
      )}

      {/* Live fleet — visibility is per asset TYPE (ADR 192): a pose's `series`
          carries its typeCode (roster seed), and the LayerPanel generates one
          toggle per type present. Stale (not seen within STALE_MS → offline)
          are hidden unless the "staleRobots" layer is enabled, so a unit that's no longer
          reporting doesn't sit frozen on the floor. Exception: an asset whose edge gateway is
          online (commissioned, camera not localized yet) is always shown. Never-seen assets
          are spread along the parking row instead of stacking at the origin. */}
      {withParkedPoses(
        Object.values(forklifts).filter((p) =>
          !hiddenTypes[p.series]
          && assetShownOnSite(hasRtls, gateways, p.id)
          && (layers.staleRobots
            || getDisplayStatus(p) !== "offline"
            || gatewayOnlineForAsset(gateways, p.id))),
        ).map((p) => (
          <Forklift
            key={p.id}
            pose={p}
            showTrail={layers.trail}
            showLabel={layers.labels}
          />
        ))}
    </EditableWarehouseGroup>
  );
}

